import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Search, Eye, Pencil, Trash2, ChevronDown, Zap, Wand2, UserCheck, AlertCircle, X, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePagamentiPendenti } from '../contexts/PagamentiPendentiContext';
import SmartFilters from '../components/SmartFilters';
import { parseApiError } from '../lib/parseApiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientiPage() {
  const { isAdmin } = useAuth();
  const { hasPagamentoPendente, getPagamentoPendenteInfo, refresh: refreshPendenti } = usePagamentiPendenti();
  const navigate = useNavigate();
  const [clienti, setClienti] = useState([]);
  const [tecnici, setTecnici] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroCitta, setFiltroCitta] = useState('');
  const [filtroTecnico, setFiltroTecnico] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [tecnicoSearch, setTecnicoSearch] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const lastLookupRef = useRef('');
  const [formData, setFormData] = useState({
    ragione_sociale: '',
    partita_iva: '',
    codice_fiscale: '',
    sdi: '',
    indirizzo_legale: '',
    citta_legale: '',
    cap_legale: '',
    provincia_legale: '',
    telefono: '',
    email: '',
    pec: '',
    note: '',
    tecnico_assegnato_id: '',
    tecnico_assegnato_nome: ''
  });

  useEffect(() => {
    fetchClienti();
    refreshPendenti();
    fetchTecnici();
  }, []);

  const fetchTecnici = async () => {
    try {
      const res = await axios.get(`${API}/tecnici`);
      setTecnici(res.data);
    } catch (error) {
      console.error('Errore caricamento tecnici:', error);
    }
  };

  const fetchClienti = async () => {
    try {
      const response = await axios.get(`${API}/clienti`);
      setClienti(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento clienti');
    } finally {
      setLoading(false);
    }
  };

  // Lookup automatico dati aziendali da Partita IVA (VIES)
  const lookupPartitaIva = async (pivaValue) => {
    const piva = (pivaValue || formData.partita_iva).replace(/\D/g, '');
    if (piva.length !== 11) {
      toast.error('Inserisci una Partita IVA valida (11 cifre)');
      return;
    }
    lastLookupRef.current = piva;
    setLookupLoading(true);
    try {
      const res = await axios.get(`${API}/clienti/lookup-piva/${piva}`);
      const d = res.data;
      setFormData(prev => ({
        ...prev,
        partita_iva: d.partita_iva,
        ragione_sociale: d.ragione_sociale || prev.ragione_sociale,
        indirizzo_legale: d.indirizzo || prev.indirizzo_legale,
        cap_legale: d.cap || prev.cap_legale,
        citta_legale: d.citta || prev.citta_legale,
        provincia_legale: d.provincia || prev.provincia_legale
      }));
      toast.success('Dati aziendali compilati da VIES — verifica e modifica se necessario');
    } catch (error) {
      toast.error(parseApiError(error, 'Ricerca P.IVA non riuscita'));
    } finally {
      setLookupLoading(false);
    }
  };

  // Auto-lookup quando si raggiunge l'11ª cifra
  const handlePivaChange = (value) => {
    setFormData(prev => ({ ...prev, partita_iva: value }));
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11 && lastLookupRef.current !== digits) {
      lookupPartitaIva(digits);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      // Convert empty strings to null for optional email fields (Pydantic EmailStr validation)
      if (data.email === '') data.email = null;
      if (data.pec === '') data.pec = null;
      if (editingCliente) {
        await axios.put(`${API}/clienti/${editingCliente.id}`, data);
        toast.success('Cliente aggiornato');
      } else {
        await axios.post(`${API}/clienti`, data);
        toast.success('Cliente creato');
      }
      setDialogOpen(false);
      resetForm();
      fetchClienti();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nel salvataggio'));
    }
  };

  const handleEdit = (cliente) => {
    setEditingCliente(cliente);
    lastLookupRef.current = (cliente.partita_iva || '').replace(/\D/g, '');
    setFormData({
      ragione_sociale: cliente.ragione_sociale || '',
      partita_iva: cliente.partita_iva || '',
      codice_fiscale: cliente.codice_fiscale || '',
      sdi: cliente.sdi || '',
      indirizzo_legale: cliente.indirizzo_legale || '',
      citta_legale: cliente.citta_legale || '',
      cap_legale: cliente.cap_legale || '',
      provincia_legale: cliente.provincia_legale || '',
      telefono: cliente.telefono || '',
      email: cliente.email || '',
      pec: cliente.pec || '',
      note: cliente.note || '',
      tecnico_assegnato_id: cliente.tecnico_assegnato_id || '',
      tecnico_assegnato_nome: cliente.tecnico_assegnato_nome || ''
    });
    setTecnicoSearch(cliente.tecnico_assegnato_nome || '');
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo cliente?')) return;
    try {
      await axios.delete(`${API}/clienti/${id}`);
      toast.success('Cliente eliminato');
      fetchClienti();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore eliminazione'));
    }
  };

  const resetForm = () => {
    setEditingCliente(null);
    setTecnicoSearch('');
    setFormData({
      ragione_sociale: '',
      partita_iva: '',
      codice_fiscale: '',
      sdi: '',
      indirizzo_legale: '',
      citta_legale: '',
      cap_legale: '',
      provincia_legale: '',
      telefono: '',
      email: '',
      pec: '',
      note: '',
      tecnico_assegnato_id: '',
      tecnico_assegnato_nome: ''
    });
  };

  // Estrai lista città uniche
  const cittaUniche = [...new Set(clienti.map(c => c.citta_legale).filter(Boolean))].sort();
  
  // Estrai lista tecnici assegnati unici
  const tecniciAssegnati = [...new Set(clienti.map(c => c.tecnico_assegnato_nome).filter(Boolean))].sort();

  const filteredClienti = clienti.filter(c => {
    const matchSearch = 
      c.ragione_sociale?.toLowerCase().includes(search.toLowerCase()) ||
      c.partita_iva?.includes(search) ||
      c.citta_legale?.toLowerCase().includes(search.toLowerCase());
    const matchCitta = !filtroCitta || c.citta_legale === filtroCitta;
    const matchTecnico = !filtroTecnico || c.tecnico_assegnato_nome === filtroTecnico;
    return matchSearch && matchCitta && matchTecnico;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground font-mono text-sm">CARICAMENTO...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clienti-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Clienti</h1>
          <p className="text-muted-foreground text-sm mt-1">{clienti.length} clienti registrati</p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            className="rounded-none font-mono uppercase tracking-wider"
            onClick={() => navigate('/clienti/nuovo')}
            data-testid="new-cliente-wizard-btn"
          >
            <Wand2 className="h-4 w-4 mr-2" />
            Nuovo Cliente
          </Button>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-none font-mono uppercase tracking-wider" data-testid="new-cliente-rapido-btn">
                <Zap className="h-4 w-4 mr-2" />
                Rapido
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-2xl rounded-none max-h-[90vh] flex flex-col">
            <DialogHeader className="shrink-0">
              <DialogTitle className="font-mono uppercase tracking-wider">
                {editingCliente ? 'Modifica Cliente' : 'Nuovo Cliente'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 gap-3">
              <div className="flex-1 min-h-0 overflow-y-auto pr-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase">Ragione Sociale *</Label>
                  <Input
                    value={formData.ragione_sociale}
                    onChange={(e) => setFormData({...formData, ragione_sociale: e.target.value})}
                    className="rounded-none"
                    required
                    data-testid="cliente-ragione-sociale"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Partita IVA</Label>
                  <div className="flex gap-1">
                    <Input
                      value={formData.partita_iva}
                      onChange={(e) => handlePivaChange(e.target.value)}
                      className="rounded-none flex-1"
                      placeholder="11 cifre"
                      data-testid="cliente-partita-iva"
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="rounded-none border-primary text-primary shrink-0"
                      onClick={lookupPartitaIva}
                      disabled={lookupLoading}
                      title="Compila automaticamente i dati aziendali (VIES)"
                      data-testid="cliente-piva-lookup-btn"
                    >
                      {lookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Codice Fiscale</Label>
                  <Input
                    value={formData.codice_fiscale}
                    onChange={(e) => setFormData({...formData, codice_fiscale: e.target.value})}
                    className="rounded-none"
                    data-testid="cliente-codice-fiscale"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">SDI (Cod. Destinatario)</Label>
                  <Input
                    value={formData.sdi}
                    onChange={(e) => setFormData({...formData, sdi: e.target.value})}
                    className="rounded-none font-mono"
                    maxLength={7}
                    data-testid="cliente-sdi"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Telefono</Label>
                  <Input
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    className="rounded-none"
                    data-testid="cliente-telefono"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="rounded-none"
                    data-testid="cliente-email"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">PEC</Label>
                  <Input
                    type="email"
                    value={formData.pec}
                    onChange={(e) => setFormData({...formData, pec: e.target.value})}
                    className="rounded-none"
                    placeholder="es. azienda@pec.it"
                    data-testid="cliente-pec"
                  />
                </div>
                <div className="md:col-span-2 border-t border-border pt-4">
                  <p className="text-xs font-mono uppercase text-muted-foreground mb-3">Sede Legale</p>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase">Indirizzo</Label>
                  <Input
                    value={formData.indirizzo_legale}
                    onChange={(e) => setFormData({...formData, indirizzo_legale: e.target.value})}
                    className="rounded-none"
                    data-testid="cliente-indirizzo"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Città</Label>
                  <Input
                    value={formData.citta_legale}
                    onChange={(e) => setFormData({...formData, citta_legale: e.target.value})}
                    className="rounded-none"
                    data-testid="cliente-citta"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs font-mono uppercase">CAP</Label>
                    <Input
                      value={formData.cap_legale}
                      onChange={(e) => setFormData({...formData, cap_legale: e.target.value})}
                      className="rounded-none"
                      data-testid="cliente-cap"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase">Prov.</Label>
                    <Input
                      value={formData.provincia_legale}
                      onChange={(e) => setFormData({...formData, provincia_legale: e.target.value})}
                      className="rounded-none"
                      data-testid="cliente-provincia"
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase">Note</Label>
                  <Textarea
                    value={formData.note}
                    onChange={(e) => setFormData({...formData, note: e.target.value})}
                    className="rounded-none"
                    data-testid="cliente-note"
                  />
                </div>
                <div className="md:col-span-2 border-t border-border pt-4">
                  <p className="text-xs font-mono uppercase text-muted-foreground mb-3">Tecnico/Agente Assegnato</p>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase">Tecnico/Agente</Label>
                  <div className="relative">
                    <Input
                      type="text"
                      value={tecnicoSearch}
                      onChange={(e) => {
                        setTecnicoSearch(e.target.value);
                        if (!e.target.value) {
                          setFormData({...formData, tecnico_assegnato_id: '', tecnico_assegnato_nome: ''});
                        }
                      }}
                      className="rounded-none"
                      placeholder="Cerca tecnico per nome..."
                      data-testid="cliente-tecnico-search"
                    />
                    {tecnicoSearch && !formData.tecnico_assegnato_id && (
                      <div className="absolute z-50 w-full mt-1 bg-background border border-border max-h-48 overflow-y-auto">
                        {tecnici
                          .filter(t => 
                            `${t.nome} ${t.cognome}`.toLowerCase().includes(tecnicoSearch.toLowerCase())
                          )
                          .map(t => (
                            <div
                              key={t.id}
                              className="px-3 py-2 hover:bg-muted cursor-pointer text-sm"
                              onClick={() => {
                                const nomeTecnico = `${t.nome} ${t.cognome}`;
                                setFormData({...formData, tecnico_assegnato_id: t.id, tecnico_assegnato_nome: nomeTecnico});
                                setTecnicoSearch(nomeTecnico);
                              }}
                            >
                              {t.nome} {t.cognome}
                              <span className="text-muted-foreground ml-2">({t.email})</span>
                            </div>
                          ))
                        }
                        {tecnici.filter(t => 
                          `${t.nome} ${t.cognome}`.toLowerCase().includes(tecnicoSearch.toLowerCase())
                        ).length === 0 && (
                          <div className="px-3 py-2 text-sm text-muted-foreground">
                            Nessun tecnico trovato
                          </div>
                        )}
                      </div>
                    )}
                    {formData.tecnico_assegnato_id && (
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          setFormData({...formData, tecnico_assegnato_id: '', tecnico_assegnato_nome: ''});
                          setTecnicoSearch('');
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-border shrink-0">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none" data-testid="cancel-cliente-btn">
                  Annulla
                </Button>
                <Button type="submit" className="rounded-none font-mono uppercase bg-[#1E2D5B] hover:bg-[#2a3d78] text-white" data-testid="save-cliente-btn">
                  {editingCliente ? 'Aggiorna' : 'Crea'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search and Filters - Mobile Optimized */}
      <div className="flex flex-col gap-3">
        {/* Search row */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-md h-10"
              data-testid="search-clienti"
            />
            {search && (
              <button 
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {/* Smart Filters */}
          <SmartFilters
            pageKey="clienti"
            currentFilters={{ citta_legale: filtroCitta, tecnico_assegnato_nome: filtroTecnico }}
            onApplyFilter={(filters) => {
              setFiltroCitta(filters.citta_legale || '');
              setFiltroTecnico(filters.tecnico_assegnato_nome || '');
            }}
            filterLabels={{ 
              citta_legale: 'Città', 
              tecnico_assegnato_nome: 'Tecnico'
            }}
          />
        </div>

        {/* Filter dropdowns row */}
        <div className="flex flex-wrap gap-2">
          <Select value={filtroCitta || "all"} onValueChange={(v) => setFiltroCitta(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full sm:w-40 rounded-md h-9 text-sm" data-testid="filter-citta">
              <SelectValue placeholder="Città" />
            </SelectTrigger>
            <SelectContent className="rounded-md max-h-60">
              <SelectItem value="all">Tutte le città</SelectItem>
              {cittaUniche.map((citta) => (
                <SelectItem key={citta} value={citta}>{citta}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filtroTecnico || "all"} onValueChange={(v) => setFiltroTecnico(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full sm:w-44 rounded-md h-9 text-sm" data-testid="filter-tecnico">
              <SelectValue placeholder="Tecnico" />
            </SelectTrigger>
            <SelectContent className="rounded-md max-h-60">
              <SelectItem value="all">Tutti i tecnici</SelectItem>
              {tecniciAssegnati.map((tecnico) => (
                <SelectItem key={tecnico} value={tecnico}>{tecnico}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Active filters indicator */}
          {(filtroCitta || filtroTecnico) && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setFiltroCitta(''); setFiltroTecnico(''); }}
              className="h-9 text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3 mr-1" />
              Reset filtri
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="border border-border overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th className="hidden sm:table-cell bg-cyan-600/10">Codice</th>
              <th className="bg-primary/10">Ragione Sociale</th>
              <th className="hidden md:table-cell bg-blue-600/10">P. IVA</th>
              <th className="hidden md:table-cell bg-emerald-600/10">Città</th>
              <th className="hidden lg:table-cell bg-amber-600/10">Telefono</th>
              <th className="hidden lg:table-cell bg-purple-600/10">Tecnico/Agente</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filteredClienti.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-muted-foreground py-8">
                  Nessun cliente trovato
                </td>
              </tr>
            ) : (
              filteredClienti.map((cliente) => (
                <tr 
                  key={cliente.id} 
                  className="hover:bg-muted transition-colors cursor-pointer"
                  onClick={() => navigate(`/clienti/${cliente.id}`)}
                >
                  <td className="hidden sm:table-cell font-mono text-xs font-bold bg-cyan-600/5 text-cyan-700 dark:text-cyan-400 border-l-4 border-l-cyan-600" data-testid={`codice-cliente-${cliente.id}`}>{cliente.codice || '-'}</td>
                  <td className="font-bold bg-primary/5 border-l-4 border-l-primary sm:border-l-0 sm:border-l-primary">
                    {cliente.codice && <span className="sm:hidden block font-mono text-[10px] font-bold text-cyan-600">{cliente.codice}</span>}
                    <div className="flex items-center gap-2">
                      {cliente.ragione_sociale}
                      {hasPagamentoPendente(cliente.id) && (
                        <span className="flex items-center gap-1 text-destructive bg-destructive/10 px-1.5 py-0.5 text-[10px] font-mono uppercase rounded" title={`Fatture non pagate: ${getPagamentoPendenteInfo(cliente.id)?.fatture_non_pagate || ''}`} data-testid={`badge-non-pagato-${cliente.id}`}>
                          <AlertCircle className="h-3 w-3" />
                          NON PAGATO{getPagamentoPendenteInfo(cliente.id)?.totale_dovuto ? ` € ${getPagamentoPendenteInfo(cliente.id).totale_dovuto.toFixed(2)}` : ''}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="hidden md:table-cell font-mono text-sm bg-blue-600/5 text-blue-600 dark:text-blue-400 border-l-4 border-l-blue-600">{cliente.partita_iva || '-'}</td>
                  <td className="hidden md:table-cell bg-emerald-600/5 text-emerald-600 dark:text-emerald-400 border-l-4 border-l-emerald-600">{cliente.citta_legale || '-'}</td>
                  <td className="hidden lg:table-cell bg-amber-600/5 text-amber-600 dark:text-amber-400 border-l-4 border-l-amber-600">{cliente.telefono || '-'}</td>
                  <td className="hidden lg:table-cell bg-purple-600/5 text-purple-600 dark:text-purple-400 border-l-4 border-l-purple-600">
                    {cliente.tecnico_assegnato_nome ? (
                      <span className="flex items-center gap-1">
                        <UserCheck className="h-3 w-3" />
                        {cliente.tecnico_assegnato_nome}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <Link to={`/clienti/${cliente.id}`}>
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none" data-testid={`view-cliente-${cliente.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                      {isAdmin() && (
                        <>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none" onClick={() => handleEdit(cliente)} data-testid={`edit-cliente-${cliente.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none text-destructive" onClick={() => handleDelete(cliente.id)} data-testid={`delete-cliente-${cliente.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
